import { useApi, useToaster, type AdminUpdateLeadRequest } from "@shopexperts/shared";
import { useMutation, useQueryClient } from "@tanstack/vue-query";

interface UpdateLeadParams {
    id: number;
    data: AdminUpdateLeadRequest;
}

/**
 * Composable for updating a lead (admin)
 */
export function useUpdateLead() {
    const api = useApi();
    const toaster = useToaster();
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
        mutationFn: ({ id, data }: UpdateLeadParams) => {
            return api.admin.leadsV2.updateLead({
                params: { id },
                body: data,
            });
        },
        onSuccess: () => {
            // Invalidate all leads-v2 queries to refresh the list
            queryClient.invalidateQueries({ queryKey: ["admin", "leads-v2"] });
            toaster.create({
                title: "Lead updated successfully",
                type: "success",
            });
        },
        onError: (error) => {
            toaster.create({
                title: "Failed to update lead",
                description: error.message || "An error occurred",
                type: "error",
            });
            console.error("[useUpdateLead] Error:", error);
        },
    });

    return {
        mutate,
        mutateAsync,
        isPending,
        error,
        data,
        reset,
    };
}
